/**
 * LA PARTIE — la boucle de jeu, côté joueur.
 *
 * LA PARTITION À TROIS ZONES (design §3) : la tranche, la page, la décision. C'est ce composant qui
 * la tient, et il la tient en HAUTEUR FIXE — `data-ecran="jeu"` sur la racine, et la feuille de
 * style verrouille le défilement de la fenêtre. Une seule chose défile, et c'est le texte, dans son
 * cadre. Un écran de jeu qu'on fait défiler est un écran où les choix se cherchent.
 *
 * UN PRINCIPE, ET IL VIENT DU SERVEUR : **toutes les routes d'une partie rendent LA MÊME VUE**.
 * Choisir, frapper, manger, se reposer, ramasser — chaque réponse est la partie entière, nœud
 * courant compris. Ce composant n'a donc jamais à recoller deux moitiés d'état : il remplace, et
 * c'est tout. C'est ce qui garantit qu'un PV affiché est un PV en base.
 *
 * Ce qu'il tient en propre : le RÉCIT DU COMBAT en cours (les événements s'accumulent tant que le
 * combat dure, sinon on ne lirait que le dernier échange), et le refus éventuel — affiché, jamais
 * avalé, et jamais effacé tout seul.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { api } from "../api/client.js";
import { useRessource } from "../api/chargement.js";
import type {
  ActionCombat,
  CarteEnVue,
  Caracteristique,
  EvenementCombat,
  HexagoneFrequente,
  LesAnnotations,
  LesTraces,
  LigneDeJournal,
  Marqueur,
  Objet,
  PartieEnVue,
  Regles
} from "../api/types.js";
import { BandeDEtat } from "../composants/BandeDEtat.js";
import { Glyphe } from "../composants/Glyphe.js";
import { Message } from "../composants/Message.js";
import { messageDe } from "../lexique/erreurs.js";
import { Lien } from "../routeur.js";
import { catalogueDe } from "../lexique/mots.js";
import { Combat } from "./partie/Combat.js";
import { FenetreDEchange } from "./partie/Echange.js";
import { FinHeureuse, Mort, Reprise } from "./partie/Fin.js";
import { LesTiroirs } from "./partie/Tiroirs.js";
import { Noeud } from "./partie/Noeud.js";
import { t } from "../lexique/langue.js";
import { useEnLigne } from "../reseau.js";

interface ReponseDePartie {
  partie: PartieEnVue;
  evenements?: EvenementCombat[];
}

export function Partie({ partieId }: { partieId: string }) {
  const [partie, poseLaPartie] = useState<PartieEnVue | null>(null);
  const [evenements, poseLesEvenements] = useState<EvenementCombat[]>([]);
  const [refus, poseLeRefus] = useState<string | null>(null);
  const [enCours, poseEnCours] = useState(false);
  /* HORS LIGNE, ON NE JOUE PAS (§14.1), et cela tient à UNE condition à UN seul endroit. `bloque`
     descend déjà dans chaque écran qui écrit ; y verser le réseau évite d'avoir à se souvenir,
     geste par geste, lequel part au serveur — et une liste à tenir à jour est une liste qu'on
     oubliera d'allonger. Les boutons restent LISIBLES et désactivés (design §3), et le bandeau
     dit pourquoi. */
  const enLigne = useEnLigne();
  const bloque = enCours || !enLigne;
  const [carte, poseLaCarte] = useState<CarteEnVue | null>(null);
  const [journal, poseLeJournal] = useState<LigneDeJournal[]>([]);
  const [annotations, poseLesAnnotations] = useState<LesAnnotations>({ miennes: [], calque: [] });
  // CE QUE LES AUTRES ONT LAISSÉ (§Q21). Deux calques, deux routes : ce qui est ICI se lit sous les
  // choix, ce qui est SUR LA CARTE se pose sur la carte. Ni l'un ni l'autre ne fait partie de la
  // vue d'une partie — une trace ne change rien à l'état du jeu, et la faire voyager dans chaque
  // réponse d'action aurait coûté deux requêtes à chaque coup d'épée.
  const [traces, poseLesTraces] = useState<LesTraces | null>(null);
  const [frequentation, poseLaFrequentation] = useState<HexagoneFrequente[]>([]);
  // L'ÉCHANGE QU'ON VIENT DE JOUER (design §3, « le combat »). Il vit ici et non dans le combat :
  // quand le dernier coup tue, le combat disparaît de la vue, et la fenêtre doit lui survivre le
  // temps qu'on lise ce qui a tué.
  const [echange, poseLEchange] = useState<{ numero: number; evenements: EvenementCombat[]; noms: Map<string, string> } | null>(null);
  const combatPrecedent = useRef<boolean>(false);
  const nomsDesCombattants = useRef<Map<string, string>>(new Map());

  const { donnees: objets } = useRessource<Objet[]>("/public/objets", (charge: { objets: Objet[] }) => charge.objets);
  const { donnees: regles } = useRessource<Regles>("/public/regles", (charge: Regles) => charge);

  const rafraichisLesAnnexes = useCallback(async () => {
    // La carte et le journal ne viennent pas avec la vue : ce sont deux routes à part, parce qu'un
    // nœud n'a aucune raison de traîner cent hexagones derrière lui à chaque clic.
    const [c, j, a, t, f] = await Promise.allSettled([
      api.lis<{ carte: CarteEnVue }>(`/parties/${partieId}/carte`),
      api.lis<{ journal: LigneDeJournal[] }>(`/parties/${partieId}/journal`),
      api.lis<LesAnnotations>(`/parties/${partieId}/annotations`),
      api.lis<LesTraces>(`/parties/${partieId}/traces`),
      api.lis<{ hexagones: HexagoneFrequente[] }>(`/parties/${partieId}/traces/carte`)
    ]);
    if (c.status === "fulfilled") poseLaCarte(c.value.carte);
    if (j.status === "fulfilled") poseLeJournal(j.value.journal);
    if (a.status === "fulfilled") poseLesAnnotations(a.value);
    // `allSettled` et non `all` : une trace qui ne se charge pas ne doit pas emporter la carte et
    // le journal avec elle. Ce qui manque ne s'affiche pas, le reste du jeu continue.
    if (t.status === "fulfilled") poseLesTraces(t.value);
    if (f.status === "fulfilled") poseLaFrequentation(f.value.hexagones);
  }, [partieId]);

  /** ANNOTER NE CHANGE PAS LA PARTIE — ni l'horloge, ni le sac, ni le nœud. On ne recharge donc que
   * les annotations : passer par `agis` bloquerait l'écran de jeu et ferait clignoter la page pour
   * un marqueur posé sur une carte. */
  const annote = useCallback(
    async (chemin: string, methode: "POST" | "PATCH" | "DELETE", corps?: unknown) => {
      poseLeRefus(null);
      try {
        await api.envoie(methode, `/parties/${partieId}/annotations${chemin}`, corps);
        const suite = await api.lis<LesAnnotations>(`/parties/${partieId}/annotations`);
        poseLesAnnotations(suite);
      } catch (erreur) {
        poseLeRefus(messageDe(erreur));
      }
    },
    [partieId]
  );

  const accueille = useCallback((recue: PartieEnVue, nouveaux?: EvenementCombat[]) => {
    const enCombat = recue.combat !== null;
    // Un combat qui s'ouvre efface le récit du précédent ; un combat qui continue l'allonge.
    if (enCombat && !combatPrecedent.current) poseLesEvenements(nouveaux ?? []);
    else if (nouveaux?.length) poseLesEvenements((avant) => [...avant, ...nouveaux]);
    combatPrecedent.current = enCombat;

    // LES NOMS SURVIVENT AU COMBAT. Le dernier coup peut tuer : le combat n'est alors plus dans la
    // vue, et la fenêtre d'échange n'aurait plus de quoi nommer celui qui a frappé.
    for (const c of recue.combat?.combattants ?? []) nomsDesCombattants.current.set(c.id, c.nom);
    if (nouveaux?.length) {
      poseLEchange((avant) => ({
        numero: (avant?.numero ?? 0) + 1,
        evenements: nouveaux,
        noms: new Map(nomsDesCombattants.current)
      }));
    }
    poseLaPartie(recue);
  }, []);

  useEffect(() => {
    let vivant = true;
    api
      .lis<ReponseDePartie>(`/parties/${partieId}`)
      .then(({ partie: recue }) => {
        if (!vivant) return;
        combatPrecedent.current = false;
        accueille(recue);
        void rafraichisLesAnnexes();
      })
      .catch((e: unknown) => vivant && poseLeRefus(messageDe(e)));
    return () => {
      vivant = false;
    };
  }, [partieId, accueille, rafraichisLesAnnexes]);

  const agis = useCallback(
    async (chemin: string, corps?: unknown) => {
      if (bloque) return;
      poseEnCours(true);
      poseLeRefus(null);
      try {
        const reponse = await api.poste<ReponseDePartie>(`/parties/${partieId}${chemin}`, corps);
        accueille(reponse.partie, reponse.evenements);
        await rafraichisLesAnnexes();
      } catch (erreur) {
        poseLeRefus(messageDe(erreur));
      } finally {
        poseEnCours(false);
      }
    },
    [bloque, partieId, accueille, rafraichisLesAnnexes]
  );

  // La partition à hauteur fixe ne vaut que pour le JEU : la mort, la fin et la reprise sont des
  // pages entières, qu'on lit et qu'on fait défiler comme le reste du livre.
  useEcranDeJeu(partie?.statut === "active");

  if (refus && !partie) {
    return (
      <div className="page page--etroite">
        <Message espece="refus">{refus}</Message>
      </div>
    );
  }
  if (!partie) return <div className="page" aria-busy="true" />;

  // Le socle public, et PAR-DESSUS les objets que la partie transporte : c'est l'aventure qui nomme
  // ce qu'elle a inventé.
  const catalogue = catalogueDe(objets ?? [], partie);

  /* LA FENÊTRE D'ÉCHANGE PASSE PAR-DESSUS TOUT, y compris la mort. Le dernier coup tue, le combat
     disparaît de la vue et l'écran de mort prend sa place : sans cela, la fenêtre s'évanouirait
     juste avant de montrer ce qui a tué — exactement le défaut qu'elle répare (design §3). On lit,
     on ferme, et la mort est là derrière. */
  const fenetre = echange ? (
    <FenetreDEchange
      numero={echange.numero}
      evenements={echange.evenements}
      noms={echange.noms}
      surFermeture={() => poseLEchange(null)}
    />
  ) : null;

  if (partie.statut === "dead") {
    return (
      <>
        {fenetre}
        <Mort partie={partie} journal={journal} carte={carte} />
      </>
    );
  }
  if (partie.statut === "finished") {
    return (
      <>
        {fenetre}
        <FinHeureuse partie={partie} journal={journal} carte={carte} />
      </>
    );
  }
  if (partie.statut === "reprise") {
    return (
      <>
        {fenetre}
        <Reprise
          partie={partie}
          journal={journal}
          bloque={bloque}
          surReprise={(cicatrice: Caracteristique) => void agis("/reprise", { cicatrice })}
        />
      </>
    );
  }

  const fiche = regles?.archetypes.find((a) => a.id === partie.heros.archetype) ?? null;

  return (
    <div className="jeu">
      {fenetre}
      <BandeDEtat
        partie={partie}
        tirettes={
          <div className="tirettes">
            {/* LA SORTIE PORTE SON PROPRE GLYPHE. Elle partageait la plume avec le journal : tant que
                les tirettes montrent leurs mots, on ne s'en aperçoit pas ; dès qu'elles les perdent
                faute de place, deux tirettes deviennent le même bouton. */}
            <Lien vers="/" className="tirette tirette--sortie" title={t("Refermer le livre et revenir à la table")}>
              <Glyphe nom="revers" taille={16} titre={t("Revenir à la table")} />
              <span className="tirette__nom capitales">{t("La table")}</span>
            </Lien>
            <LesTiroirs
              partie={partie}
              catalogue={catalogue}
              carte={carte}
              journal={journal}
              annotations={annotations}
              frequentation={frequentation}
              bloque={bloque}
              surRepas={() => void agis("/repas")}
              surRepos={(type) => void agis("/repos", { type })}
              surUtilise={(objetId) => void agis("/utilise", { objetId })}
              surLit={(objetId, blessure) => void agis("/lit", { objetId, ...(blessure ? { blessure } : {}) })}
              surSoin={(blessure) => void agis("/soin", { blessure })}
              surAllume={(objetId) => void agis("/allume", { objetId })}
              surEteint={() => void agis("/eteint")}
              surEquipe={(objetId, clef, retire) => void agis("/equipement", { objetId, clef, retire })}
              surJette={(objetId, quantite) => void agis("/jette", { objetId, quantite })}
              surAnnote={(hexagone: string, marqueur: Marqueur, note: string, publique: boolean) =>
                void annote("", "POST", { hexagone, marqueur, note, publique })
              }
              surResout={(id: string, resolue: boolean) => void annote(`/${id}`, "PATCH", { resolue })}
              surPublie={(id: string, publique: boolean) => void annote(`/${id}`, "PATCH", { publique })}
              surEfface={(id: string) => void annote(`/${id}`, "DELETE")}
              surVote={(id: string, pour: boolean) => void annote(`/${id}/voix`, "POST", { pour })}
            />
          </div>
        }
      />

      {/* CE QUI DOIT ÊTRE SU S'ÉCRIT DANS LA PAGE, jamais dans un tiroir qui surgit : un refus avalé
          ou une mention qui disparaît toute seule, dans un jeu où l'on meurt pour de bon, c'est une
          information manquée — donc une mort. */}
      {refus || partie.pacte ? (
        <div className="jeu__annonces">
          {refus ? (
            <Message espece="refus" surFermeture={() => poseLeRefus(null)}>
              {refus}
            </Message>
          ) : null}
          {partie.pacte ? (
            <p className="pacte capitales" title={t("Vous êtes entré dans cette aventure en dessous de son seuil de puissance : elle ne s'est pas adoucie pour autant.")}>
              {t("Pacte de sous-équipement")}
            </p>
          ) : null}
        </div>
      ) : null}

      {partie.combat ? (
        <Combat
          combat={partie.combat}
          heros={partie.heros}
          catalogue={catalogue}
          evenements={evenements}
          capacite={fiche ? { nom: fiche.capacite.nom, cout: fiche.capacite.cout } : null}
          chiffres={regles?.combat ?? null}
          bloque={bloque}
          surAction={(action: ActionCombat) => void agis("/combat", { action })}
        />
      ) : partie.noeud ? (
        <Noeud
          noeud={partie.noeud}
          catalogue={catalogue}
          bloque={bloque}
          partieId={partie.id}
          nuit={partie.horloge.nuit}
          rencontre={partie.rencontre}
          traces={traces}
          surEngage={() => void agis("/engage")}
          surChoix={(choixId) => void agis("/choix", { choixId })}
          surFouille={(cacheId, reponse) => void agis("/fouille", { cacheId, ...(reponse ? { reponse } : {}) })}
          surPrends={(cacheId, objetId) => void agis("/prends", { cacheId, objetId })}
          surFouilleLesLieux={() => void agis("/fouille-les-lieux")}
        />
      ) : (
        <p className="douce" style={{ padding: "var(--e5)" }}>
          {t("Cette partie n'a plus de nœud courant.")}
        </p>
      )}
    </div>
  );
}

/**
 * L'ÉCRAN DE JEU SE VERROUILLE EN HAUTEUR, et il se déverrouille en partant. La marque est posée sur
 * la racine du document plutôt que sur un conteneur : c'est `body` qu'il faut empêcher de défiler,
 * et une classe posée plus bas ne l'atteindrait pas. Elle se retire au démontage, sans quoi le
 * cimetière hériterait d'une fenêtre bloquée.
 */
function useEcranDeJeu(actif: boolean): void {
  useEffect(() => {
    if (!actif) return;
    document.documentElement.dataset.ecran = "jeu";
    return () => {
      delete document.documentElement.dataset.ecran;
    };
  }, [actif]);
}
